import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { user_reset } from '../store/reducers/authReducer';
import { reset_count, messageClear } from '../store/reducers/cardReducer';
import { get_orders } from '../store/reducers/orderReducer';
import { DISPLAY, Container, Heart, WhatsApp } from '../components/home/ui';
import { BoxIcon } from '../components/account/shared';

// "Mi cuenta" (diseño cuenta.html): barra lateral + la vista de la ruta.

const GridIcon = (p) => (
    <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></svg>
);
const LockIcon = (p) => (
    <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
);
const LogoutIcon = (p) => (
    <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>
);

const Dashboard = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { userInfo } = useSelector(state => state.auth);
    const { wishlist_count, successMessage, errorMessage } = useSelector(state => state.card);
    const { myOrders } = useSelector(state => state.order);
    const [confirmOut, setConfirmOut] = useState(false);

    useEffect(() => {
        if (userInfo) dispatch(get_orders({ status: 'all', customerId: userInfo.id }));
    }, []);

    // Avisos del carrito y favoritos de todas las vistas de la cuenta.
    useEffect(() => {
        if (successMessage) { toast.success(successMessage); dispatch(messageClear()); }
        if (errorMessage) { toast.error(errorMessage); dispatch(messageClear()); }
    }, [successMessage, errorMessage]);

    useEffect(() => {
        if (!confirmOut) return;
        const onKey = (e) => { if (e.key === 'Escape') setConfirmOut(false); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [confirmOut]);

    // El token del cliente es un JWT sin sesión en el servidor: cerrar sesión
    // es olvidarlo. (Antes llamaba a /customer/logout, que no existe, y el
    // error impedía salir.)
    const logout = () => {
        localStorage.removeItem('customerToken');
        dispatch(user_reset());
        dispatch(reset_count());
        setConfirmOut(false);
        navigate('/login');
    };

    const nombre = userInfo?.name || '';
    const links = [
        ['/dashboard', 'Resumen', GridIcon, null, true],
        ['/dashboard/my-orders', 'Mis pedidos', BoxIcon, myOrders.length],
        ['/dashboard/my-wishlist', 'Favoritos', Heart, wishlist_count],
        ['/dashboard/chat', 'Chat con soporte', WhatsApp],
        ['/dashboard/change-password', 'Cambiar contraseña', LockIcon],
    ];

    return (
        <div className='w-full bg-white'>
            <Header />
            <div className='bg-[#faf9f6] pt-8 pb-20 border-t border-[#f1efea]'>
                <Container>
                    <div className='grid grid-cols-[270px_minmax(0,1fr)] md-lg:grid-cols-1 gap-7 items-start'>
                        <aside className='bg-white border border-[#f1efea] rounded-[20px] p-[18px] sticky top-[150px] md-lg:static md-lg:p-3'>
                            <div className='flex gap-3 items-center px-1 pt-1 pb-[18px] border-b border-[#f1efea] mb-[10px] md-lg:hidden'>
                                <div className='w-12 h-12 rounded-full bg-[#f87171] text-white grid place-items-center font-extrabold text-[19px] flex-shrink-0' style={DISPLAY}>
                                    {(nombre[0] || '🐾').toUpperCase()}
                                </div>
                                <div className='min-w-0'>
                                    <b className='block text-[#0f172a] text-[15px] truncate'>{nombre}</b>
                                    <span className='block text-[12.5px] text-[#64748b] truncate'>{userInfo?.email}</span>
                                </div>
                            </div>
                            <nav className='flex flex-col gap-[2px] md-lg:flex-row md-lg:overflow-x-auto md-lg:gap-1 [scrollbar-width:none]'>
                                {links.map(([to, label, Icon, badge, end]) => (
                                    <NavLink key={to} to={to} end={end}
                                        className={({ isActive }) => `flex items-center gap-3 px-3 py-[11px] md-lg:py-[10px] rounded-[12px] text-[14.5px] font-semibold whitespace-nowrap transition-all ${isActive ? 'bg-[#ecfeff] text-[#0e7490] [&_svg]:text-[#0891b2]' : 'text-[#334155] hover:bg-[#faf9f6] hover:text-[#0f172a]'}`}>
                                        <Icon className='w-[18px] h-[18px] text-[#64748b] flex-shrink-0' />
                                        {label}
                                        {badge > 0 && <span className='ml-auto text-[11px] font-extrabold bg-[#f1efea] text-[#64748b] rounded-full min-w-[20px] h-5 grid place-items-center px-[6px]'>{badge}</span>}
                                    </NavLink>
                                ))}
                                <span className='h-px bg-[#f1efea] my-2 md-lg:hidden' />
                                <button type='button' onClick={() => setConfirmOut(true)}
                                    className='flex items-center gap-3 px-3 py-[11px] md-lg:py-[10px] rounded-[12px] text-[14.5px] font-semibold text-[#ef4444] whitespace-nowrap hover:bg-[#fef2f2] text-left'>
                                    <LogoutIcon className='w-[18px] h-[18px] flex-shrink-0' />Cerrar sesión
                                </button>
                            </nav>
                        </aside>

                        <div className='min-w-0'>
                            <Outlet />
                        </div>
                    </div>
                </Container>
            </div>

            {confirmOut && (
                <div role='dialog' aria-modal='true' aria-labelledby='logout-t' onClick={e => { if (e.target === e.currentTarget) setConfirmOut(false); }}
                    className='fixed inset-0 bg-[#0f172a]/45 grid place-items-center z-[10000] p-5'>
                    <div className='bg-white rounded-[22px] p-7 max-w-[400px] w-full text-center'>
                        <div className='w-14 h-14 rounded-full bg-[#fee2e2] text-[#ef4444] grid place-items-center mx-auto mb-[14px]'><LogoutIcon className='w-6 h-6' /></div>
                        <h3 id='logout-t' className='text-[#1e3a8a] text-[22px] font-bold mt-0 mb-[6px]' style={DISPLAY}>¿Cerrar sesión?</h3>
                        <p className='text-[#64748b] text-[14.5px] mt-0 mb-[22px]'>Tu carrito y favoritos se quedan guardados para la próxima vez.</p>
                        <div className='flex gap-[10px]'>
                            <button type='button' autoFocus onClick={() => setConfirmOut(false)} className='flex-1 h-12 rounded-[12px] font-bold text-[14.5px] border-[1.5px] border-[#e8e6e1] text-[#1e3a8a]'>Cancelar</button>
                            <button type='button' onClick={logout} className='flex-1 h-12 rounded-[12px] font-bold text-[14.5px] bg-[#ef4444] text-white'>Cerrar sesión</button>
                        </div>
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
};

export default Dashboard;
